import React, { ReactNode } from 'react'
import { RecoveryIllustration } from './RecoveryIllustration'
import { RecoveryPage, RecoveryPageButton } from './RecoveryPage'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'

interface ErrorBoundaryProps {
  children: ReactNode
  fallbackUI?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

const DefaultFallbackUI: React.FC = (): JSX.Element => {
  const t = useAppTranslations()

  return (
    <RecoveryPage
      eyebrow={t('ErrorBoundaryUIFallback.eyebrow')}
      title={t('ErrorBoundaryUIFallback.somethingWentWrong')}
      description={t('ErrorBoundaryUIFallback.apologizeInconvenience')}
      illustration={<RecoveryIllustration variant="error" />}
      actions={
        <RecoveryPageButton label={t('ErrorBoundaryUIFallback.refreshPage')} onClick={() => window.location.reload()} />
      }
    />
  )
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // TODO connect to a report error service
    console.error('Caught by error boundary:', error, errorInfo)
  }

  render(): ReactNode {
    if (this.state.hasError) return this.props.fallbackUI || <DefaultFallbackUI />

    return this.props.children
  }
}
